import { lazy, Suspense, useEffect } from "react";
import BohoNav from "@/components/BohoNav";
import BohoHeroOptimized from "@/components/BohoHeroOptimized";
import LoadingSpinner from "@/components/LoadingSpinner";
import SEO from "@/components/SEO";
import { useSelectiveShopify } from "@/hooks/useSelectiveShopify";
import { productionLogger } from '@/utils/production-logger';

import { Sparkles, Shield, Truck } from "lucide-react";
import RevisionBadge from "@/components/RevisionBadge";
import CherryBlossomDivider from "@/components/CherryBlossomDivider";

// Code-split heavy components to reduce main bundle size
const FeaturedProductsOptimized = lazy(() => import("@/components/FeaturedProductsOptimized"));
const BohoFooter = lazy(() => import("@/components/BohoFooter"));
const PerformanceMonitor = lazy(() => import("@/components/PerformanceMonitor"));

const Index = () => {
  const { loadingFeatured } = useSelectiveShopify();


  // Simple performance optimizations without dynamic imports
  useEffect(() => {
    // SSR safety check
    if (typeof document === 'undefined' || typeof window === 'undefined') return;
    
    // Basic font preloading
    const preloadFonts = () => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = 'https://fonts.googleapis.com';
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    };

    if (window.requestIdleCallback) {
      requestIdleCallback(preloadFonts, { timeout: 2000 });
    } else {
      setTimeout(preloadFonts, 100);
    }
  }, []);

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://bohostyleco.com';
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Boho Style Co.",
    "url": origin,
    "description": "Handcrafted bohemian fashion, jewelry, and lifestyle accessories",
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${origin}/products?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    },
    "sameAs": [
      "https://facebook.com/bohostyleco",
      "https://instagram.com/bohostyleco"
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Plus Size Boho Fashion & Jewelry | Inclusive Bohemian Style | Boho Style Co."
        description="Discover plus size bohemian fashion, jewelry & lifestyle pieces for every woman. Shop inclusive boho style, curvy-friendly designs, and sustainable accessories. Free shipping on orders $150+"
        image="/og-image.jpg"
        structuredData={structuredData}
      />
      
      <BohoNav />
      <BohoHeroOptimized />
      
      <CherryBlossomDivider size="sm" />
      
      {/* Value Propositions */}
      <section className="section-spacing bg-muted/30">
        <div className="container-padding">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center element-spacing-standard">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <h3 className="section-header text-primary-color mb-2">Handcrafted Quality</h3>
              <p className="body-text text-muted-color">Each piece is carefully selected and crafted with attention to detail</p>
            </div>
            <div className="text-center element-spacing-standard">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <h3 className="section-header text-primary-color mb-2">Secure Shopping</h3>
              <p className="body-text text-muted-color">Safe and secure checkout with buyer protection</p>
            </div>
            <div className="text-center element-spacing-standard">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center">
                <Truck className="w-8 h-8 text-primary" />
              </div>
              <h3 className="section-header text-primary-color mb-2">Free Shipping</h3>
              <p className="body-text text-muted-color">Free shipping on orders over $150</p>
            </div>
          </div>
        </div>
      </section>

      <Suspense fallback={<LoadingSpinner />}>
        <FeaturedProductsOptimized />
      </Suspense>

      <CherryBlossomDivider size="lg" />

      <Suspense fallback={<LoadingSpinner />}>
        <BohoFooter />
      </Suspense>

      <RevisionBadge pageKey="homepage" />
      
      <Suspense fallback={null}>
        <PerformanceMonitor />
      </Suspense>
    </div>
  );
};

export default Index;
